import React, {
  MouseEvent,
  useState,
  useRef,
  ChangeEvent,
  useEffect,
  forwardRef,
} from 'react';
import { useDropzone } from 'react-dropzone';
import Box from '@mui/material/Box';
import InputBase from '@mui/material/InputBase';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { IEmojiData } from 'emoji-picker-react';

import EmojiButton from './components/EmojiButton';
import VariablesButton from './components/VariablesButton';
import InsertImageButton from './components/InsertImageButton';
import SendButton from './components/SendButton';
import UrlShortenerButton from './components/UrlShortenerButton';
import InputArea from './components/InputArea/InputArea';

export type SmsEditorProps = {
  value: string;
  sendable?: boolean;
  enableUrlShortener?: boolean;
  enableInsertImage?: boolean;
  handleAddEmoji: (emoji: string) => void;
  handleInputChange: (event: { target: { value: string } }) => void;
  variables?: Record<string, { id: string; display: string }[]>;
  imageAttachment?: File | null;
  handleConcatInputValue: (concatVal: string) => void;
  handleSend?: () => void;
  handleInsertImage?: (file: File) => void;
  handleDeleteImage?: () => void;
  isSendButtonLoading?: boolean;
  fullWidth?: boolean;
  error?: boolean;
  errorText?: string;
  setErrorText?: React.Dispatch<React.SetStateAction<string>>;
};

export type Ref = HTMLTextAreaElement;

export const SmsEditor = forwardRef<Ref, SmsEditorProps>(
  (
    {
      value = '',
      sendable = true,
      enableUrlShortener = true,
      enableInsertImage = true,
      handleAddEmoji,
      handleInputChange,
      handleConcatInputValue,
      handleInsertImage,
      handleDeleteImage,
      variables,
      imageAttachment,
      handleSend,
      isSendButtonLoading,
      fullWidth = false,
      error = false,
      errorText,
      setErrorText,
    },
    inputRef
  ) => {
    const editorRef = useRef<HTMLDivElement | null>(null);
    useState<HTMLDivElement | null>(null);

    const [active, setActive] = useState(false);

    const handleClickEmoji = (event: MouseEvent, emojiData: IEmojiData) => {
      handleAddEmoji(emojiData.emoji);
    };

    const borderColor = () => {
      if (active && !error) {
        return '#5a5a5a';
      }

      if (error) {
        return 'error.main';
      }

      return '#9a9a9a';
    };

    const handleChangeAttachment = (event: ChangeEvent<HTMLInputElement>) => {
      if (handleInsertImage) {
        handleInsertImage(event.target.files?.[0] as File);
      }
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      maxFiles: 1,
      accept: { 'image/*': [] },
      noClick: true,
      onDrop: (acceptedFiles, fileRejections, event) => {
        if (Boolean(fileRejections.length) && setErrorText) {
          setErrorText('Unsupported file type. Please only insert images.');
        }
        if (Boolean(acceptedFiles) && handleInsertImage) {
          handleInsertImage(acceptedFiles[0] as File);
        }
      },
    });

    useEffect(() => {
      if (Boolean(imageAttachment) && setErrorText) {
        setErrorText('');
      }
    }, [imageAttachment]);

    return (
      <>
        {isDragActive && enableInsertImage ? (
          <>
            <div
              {...getRootProps({
                style: {
                  width: '100%',
                  height: `${editorRef.current?.offsetHeight}px`,
                  border: '3px dashed #5a5a5a',
                  borderRadius: '8px',
                  boxSizing: 'border-box',
                  position: 'relative',
                },
              })}
            >
              <input {...getInputProps()} />
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                }}
              >
                <Typography variant="h5">Drop your image</Typography>
              </Box>
            </div>
          </>
        ) : (
          <>
            <div {...getRootProps()} style={{ width: '100%' }}>
              <ClickAwayListener onClickAway={() => setActive(false)}>
                <Box
                  ref={editorRef}
                  sx={{
                    border: '2px solid #eee',
                    borderRadius: '8px',
                    p: 1,
                    height: 'fit-content',
                    width: fullWidth ? '100%' : undefined,
                    borderColor: borderColor(),
                    boxSizing: 'border-box',
                  }}
                >
                  <Box
                    id="sms-editor-header"
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    {Boolean(variables) && (
                      <VariablesButton
                        variables={
                          variables as Record<
                            string,
                            { id: string; display: string }[]
                          >
                        }
                        handleConcatInputValue={handleConcatInputValue}
                      />
                    )}

                    {enableUrlShortener && (
                      <Divider
                        orientation="vertical"
                        variant="middle"
                        flexItem
                      />
                    )}

                    {enableUrlShortener && <UrlShortenerButton />}
                  </Box>

                  {variables ? (
                    <InputArea
                      value={value}
                      onChange={(e) => handleInputChange(e)}
                      placeholder="Enter your message"
                      ref={inputRef}
                      data={
                        variables as Record<
                          string,
                          { id: string; display: string }[]
                        >
                      }
                      setActive={setActive}
                    />
                  ) : (
                    <InputBase
                      id="sms-input"
                      inputRef={inputRef}
                      multiline
                      rows={4}
                      inputProps={{
                        style: { margin: '10px 0', height: 'fit-content' },
                      }}
                      placeholder="Enter your message"
                      value={value}
                      onChange={(e) => handleInputChange(e)}
                      fullWidth
                    />
                  )}

                  <Box
                    id="sms-editor-footer"
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Box sx={{ display: 'flex' }}>
                      <EmojiButton handleClickEmoji={handleClickEmoji} />

                      {enableInsertImage && (
                        <InsertImageButton
                          imgFile={imageAttachment as File}
                          onAttachmentChange={handleChangeAttachment}
                          onDeleteAttachment={handleDeleteImage as () => void}
                        />
                      )}
                    </Box>

                    {sendable && (
                      <SendButton
                        loading={isSendButtonLoading}
                        disabled={!Boolean(value.length)}
                        onSend={handleSend}
                      />
                    )}
                  </Box>
                </Box>
              </ClickAwayListener>
              <Typography variant="caption" sx={{ color: 'error.main' }}>
                {errorText}
              </Typography>
            </div>
          </>
        )}
      </>
    );
  }
);
